/**
 * Quotes Database Service
 * Handles persistence of quote data to SQLite database
 */

import type { QuoteData } from "@/services/market-data-streamer";
import type { CachedQuote } from "./quotes-cache-service";

export interface SymbolMapping {
  positionSymbol: string;
  streamerSymbol: string;
  symbolType?: string;
}

export interface QuoteHistory {
  id: number;
  symbol: string;
  bidPrice: number | null;
  askPrice: number | null;
  lastPrice: number | null;
  timestamp: number;
  isValid: boolean;
  confidence: number;
}

interface StoredQuoteRecord {
  symbol: string;
  streamer_symbol: string;
  bid_price: number | null;
  ask_price: number | null;
  bid_size: number | null;
  ask_size: number | null;
  last_price: number | null;
  mid_price: number | null;
  spread: number | null;
  spread_percentage: number | null;
  volume: number | null;
  timestamp: number;
  update_count: number;
  last_valid_update: number;
  confidence: number;
}

interface StoredQuoteHistoryRecord {
  id: number;
  symbol: string;
  bid_price: number | null;
  ask_price: number | null;
  last_price: number | null;
  timestamp: number;
  is_valid: boolean;
  confidence: number;
}

interface StoredMappingRecord {
  position_symbol?: string;
  streamer_symbol?: string;
  positionSymbol?: string;
  streamerSymbol?: string;
  symbol_type?: string;
  symbolType?: string;
}

interface QuoteCacheResponse {
  quotes?: Array<StoredQuoteRecord | CachedQuote> | Record<string, StoredQuoteRecord | CachedQuote>;
}

interface QuoteHistoryResponse {
  history?: StoredQuoteHistoryRecord[];
}

interface SymbolMappingsResponse {
  mappings?: StoredMappingRecord[];
}

const isStoredQuoteRecord = (value: unknown): value is StoredQuoteRecord =>
  typeof value === "object" && value !== null &&
  typeof (value as StoredQuoteRecord).symbol === "string" &&
  typeof (value as StoredQuoteRecord).streamer_symbol === "string";

const isCachedQuoteRecord = (value: unknown): value is CachedQuote =>
  typeof value === "object" && value !== null &&
  typeof (value as CachedQuote).symbol === "string" &&
  typeof (value as CachedQuote).streamerSymbol === "string";

const isStoredQuoteHistoryRecord = (
  value: unknown,
): value is StoredQuoteHistoryRecord =>
  typeof value === "object" && value !== null &&
  typeof (value as StoredQuoteHistoryRecord).id === "number" &&
  typeof (value as StoredQuoteHistoryRecord).symbol === "string";

const isStoredMappingRecord = (value: unknown): value is StoredMappingRecord =>
  typeof value === "object" && value !== null &&
  (
    typeof (value as StoredMappingRecord).position_symbol === "string" ||
    typeof (value as StoredMappingRecord).positionSymbol === "string"
  ) &&
  (
    typeof (value as StoredMappingRecord).streamer_symbol === "string" ||
    typeof (value as StoredMappingRecord).streamerSymbol === "string"
  );

class QuotesDatabaseService {
  private readonly dbEndpoint = "/api/market-data/quotes";
  private pendingCacheUpdates = new Map<string, CachedQuote>();
  private pendingHistoryUpdates: Array<{
    symbol: string;
    quoteData: QuoteData;
    isValid: boolean;
    validationReason?: string;
    confidence?: number;
  }> = [];
  private batchSaveInterval: NodeJS.Timeout | null = null;
  private readonly BATCH_SAVE_INTERVAL = 60000; // 60 seconds - reduced from 5 seconds to minimize database writes
  private readonly MAX_BATCH_SIZE = 1000; // Increased from 100 to accommodate longer batch interval
  private lastErrorLog = 0;
  private errorLogThrottle = 60000; // Log errors at most once per minute
  private errorCount = 0;

  constructor() {
    // Start batch save timer if in browser
    if (typeof window !== "undefined") {
      this.batchSaveInterval = setInterval(() => {
        this.flushPendingUpdates();
      }, this.BATCH_SAVE_INTERVAL);
    }
  }

  /**
   * Store quote cache in database (batched)
   */
  async storeQuoteCache(symbol: string, quote: CachedQuote): Promise<void> {
    // Skip database operations on server-side
    if (typeof window === "undefined") {
      return;
    }

    // Add to pending updates
    this.pendingCacheUpdates.set(symbol, quote);

    // Flush if batch is getting too large
    if (this.pendingCacheUpdates.size >= this.MAX_BATCH_SIZE) {
      await this.flushPendingUpdates();
    }
  }

  /**
   * Store quote history in database (batched)
   */
  async storeQuoteHistory(
    symbol: string,
    quoteData: QuoteData,
    isValid: boolean,
    validationReason?: string,
    confidence?: number,
  ): Promise<void> {
    // Skip database operations on server-side
    if (typeof window === "undefined") {
      return;
    }

    // Add to pending updates
    this.pendingHistoryUpdates.push({
      symbol,
      quoteData,
      isValid,
      validationReason,
      confidence,
    });

    // Flush if batch is getting too large
    if (this.pendingHistoryUpdates.length >= this.MAX_BATCH_SIZE) {
      await this.flushPendingUpdates();
    }
  }

  /**
   * Flush all pending updates to database
   */
  private async flushPendingUpdates(): Promise<void> {
    // Save cache updates
    if (this.pendingCacheUpdates.size > 0) {
      const quotes = Array.from(this.pendingCacheUpdates.values());
      this.pendingCacheUpdates.clear();

      try {
        const response = await fetch(this.dbEndpoint, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "saveBatch",
            data: quotes,
          }),
        });

        if (!response.ok) {
          let errorData: unknown;
          try {
            errorData = (await response.json()) as unknown;
          } catch {
            errorData = { 
              error: response.statusText, 
              status: response.status,
              url: response.url 
            };
          }
          this.logThrottledError("Quote cache batch storage error:", errorData);
        }
      } catch (error) {
        this.logThrottledError("Error storing quote cache batch:", error);
      }
    }

    // Save history updates (skip for now to reduce load)
    // History is less critical than real-time cache
    if (this.pendingHistoryUpdates.length > 100) {
      // Only save history when we have a lot of updates
      this.pendingHistoryUpdates = []; // Clear without saving to reduce load
    }
  }

  /**
   * Force flush all pending updates
   */
  async flush(): Promise<void> {
    await this.flushPendingUpdates();
  }

  /**
   * Log errors with throttling to prevent console spam
   */
  private logThrottledError(message: string, error: unknown): void {
    this.errorCount++;
    const now = Date.now();

    if (now - this.lastErrorLog > this.errorLogThrottle) {
      if (this.errorCount > 1) {
        console.error(
          `${message} (${this.errorCount} errors in last minute):`,
          error,
        );
      } else {
        console.error(message, error);
      }
      this.lastErrorLog = now;
      this.errorCount = 0;
    }
  }

  /**
   * Clean up on unmount
   */
  destroy(): void {
    if (this.batchSaveInterval) {
      clearInterval(this.batchSaveInterval);
      this.batchSaveInterval = null;
    }
    // Flush any remaining updates
    this.flushPendingUpdates();
  }

  /**
   * Get cached quotes from database
   */
  async getCachedQuotes(): Promise<Map<string, CachedQuote>> {
    try {
      const response = await fetch(`${this.dbEndpoint}?action=getCachedQuotes`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to get cached quotes: ${response.statusText}`);
      }

      const data = (await response.json()) as QuoteCacheResponse;
      const quotes = new Map<string, CachedQuote>();

      const quoteRecords = Array.isArray(data.quotes)
        ? data.quotes
        : data.quotes && typeof data.quotes === "object"
          ? Object.values(data.quotes)
          : [];

      if (quoteRecords.length > 0) {
        quoteRecords
          .forEach((quote) => {
            if (isCachedQuoteRecord(quote)) {
              quotes.set(quote.symbol, quote);
              return;
            }

            if (!isStoredQuoteRecord(quote)) {
              return;
            }

            quotes.set(quote.symbol, {
              symbol: quote.symbol,
              streamerSymbol: quote.streamer_symbol,
              bidPrice: quote.bid_price,
              askPrice: quote.ask_price,
              bidSize: quote.bid_size,
              askSize: quote.ask_size,
              lastPrice: quote.last_price,
              midPrice: quote.mid_price,
              spread: quote.spread,
              spreadPercentage: quote.spread_percentage,
              volume: quote.volume,
              timestamp: quote.timestamp,
              updateCount: quote.update_count,
              lastValidUpdate: quote.last_valid_update,
              confidence: quote.confidence,
            });
          });
      }

      return quotes;
    } catch (error) {
      console.error("Error getting cached quotes:", error);
      return new Map();
    }
  }

  /**
   * Get quote history for a symbol
   */
  async getQuoteHistory(
    symbol: string,
    hours: number = 24,
  ): Promise<QuoteHistory[]> {
    try {
      const response = await fetch(
        `${this.dbEndpoint}?action=getQuoteHistory&symbol=${encodeURIComponent(symbol)}&hours=${hours}`,
        { credentials: "include" },
      );

      if (!response.ok) {
        throw new Error(`Failed to get quote history: ${response.statusText}`);
      }

      const data = (await response.json()) as QuoteHistoryResponse;

      if (data.history && Array.isArray(data.history)) {
        return data.history
          .filter(isStoredQuoteHistoryRecord)
          .map((item: StoredQuoteHistoryRecord) => ({
            id: item.id,
            symbol: item.symbol,
            bidPrice: item.bid_price,
            askPrice: item.ask_price,
            lastPrice: item.last_price,
            timestamp: item.timestamp,
            isValid: item.is_valid,
            confidence: item.confidence,
          }));
      }

      return [];
    } catch (error) {
      console.error("Error getting quote history:", error);
      return [];
    }
  }

  /**
   * Store symbol mapping
   */
  async storeSymbolMapping(
    positionSymbol: string,
    streamerSymbol: string,
    symbolType?: string,
  ): Promise<void> {
    // Skip database operations on server-side
    if (typeof window === "undefined") {
      return;
    }

    try {
      const response = await fetch(this.dbEndpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "storeSymbolMapping",
          data: {
            positionSymbol,
            streamerSymbol,
            symbolType,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to store symbol mapping: ${response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Error storing symbol mapping:", error);
    }
  }

  /**
   * Get all symbol mappings
   */
  async getSymbolMappings(): Promise<SymbolMapping[]> {
    try {
      const response = await fetch(
        `${this.dbEndpoint}?action=getSymbolMappings`,
        { credentials: "include" },
      );

      if (response.status === 401 || response.status === 403) {
        return [];
      }

      if (!response.ok) {
        console.warn(
          `Skipping symbol mappings load: ${response.status} ${response.statusText}`.trim(),
        );
        return [];
      }

      const data = (await response.json()) as SymbolMappingsResponse;

      if (data.mappings && Array.isArray(data.mappings)) {
        return data.mappings
          .filter(isStoredMappingRecord)
          .map((mapping: StoredMappingRecord) => ({
            positionSymbol: mapping.position_symbol ?? mapping.positionSymbol ?? "",
            streamerSymbol: mapping.streamer_symbol ?? mapping.streamerSymbol ?? "",
            symbolType: mapping.symbol_type ?? mapping.symbolType,
          }));
      }

      return [];
    } catch (error) {
      console.warn("Skipping symbol mappings load:", error);
      return [];
    }
  }

  /**
   * Clean up old quote history
   */
  async cleanupOldHistory(daysToKeep: number = 7): Promise<void> {
    // Skip database operations on server-side
    if (typeof window === "undefined") {
      return;
    }

    try {
      const response = await fetch(this.dbEndpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "cleanupQuoteHistory",
          data: {
            daysToKeep,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to cleanup quote history: ${response.statusText}`,
        );
      }

      const data = await response.json();
      console.log(
        `Cleaned up ${data.deletedCount || 0} old quote history records`,
      );
    } catch (error) {
      console.error("Error cleaning up quote history:", error);
    }
  }

  /**
   * Get quote statistics for a symbol
   */
  async getQuoteStats(
    symbol: string,
    hours: number = 24,
  ): Promise<{
    avgSpread: number;
    avgSpreadPercentage: number;
    highPrice: number;
    lowPrice: number;
    updateCount: number;
    validQuotePercentage: number;
  } | null> {
    try {
      const response = await fetch(
        `${this.dbEndpoint}?action=getQuoteStats&symbol=${encodeURIComponent(symbol)}&hours=${hours}`,
        { credentials: "include" },
      );

      if (!response.ok) {
        throw new Error(`Failed to get quote stats: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.stats) {
        return {
          avgSpread: data.stats.avg_spread || 0,
          avgSpreadPercentage: data.stats.avg_spread_percentage || 0,
          highPrice: data.stats.high_price || 0,
          lowPrice: data.stats.low_price || 0,
          updateCount: data.stats.update_count || 0,
          validQuotePercentage: data.stats.valid_quote_percentage || 0,
        };
      }

      return null;
    } catch (error) {
      console.error("Error getting quote stats:", error);
      return null;
    }
  }
}

// Create singleton instance
export const quotesDatabaseService = new QuotesDatabaseService();

// Clean up on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    quotesDatabaseService.flush();
  });
}
